import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { usePageTitle } from "@/routes/__root";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/hooks";
import { businessName, legalEntity, supportEmail } from "@/lib/config";

const UPDATED = "18 October 2026";

const Mail = () => (
  <a href={`mailto:${supportEmail}`} className="underline underline-offset-4">
    {supportEmail}
  </a>
);

function LegalLayout({ eyebrow = "Legal", title, intro, children }: { eyebrow?: string; title: string; intro: ReactNode; children: ReactNode }) {
  usePageTitle(title);
  return (
    <>
      <section className="surface-ink">
        <div className="mx-auto max-w-4xl px-5 py-16 md:py-20">
          <p className="eyebrow text-gold">{eyebrow}</p>
          <h1 className="mt-4 text-4xl leading-[1.05] text-ink-foreground md:text-5xl">{title}</h1>
          <div className="hairline-gold mt-6" />
          <p className="mt-6 max-w-2xl text-ink-foreground/70">{intro}</p>
          <p className="mt-4 text-xs text-ink-foreground/50">Last updated: {UPDATED}</p>
        </div>
      </section>
      <article className="mx-auto max-w-4xl px-5 py-14 text-[0.95rem] leading-relaxed text-muted-foreground [&_h2]:mb-3 [&_h2]:mt-10 [&_h2]:text-2xl [&_h2]:text-foreground [&_li]:mt-1.5 [&_p]:mt-3 [&_strong]:text-foreground [&_ul]:mt-3 [&_ul]:list-disc [&_ul]:pl-6">
        {children}
      </article>
    </>
  );
}

const Company = () => (
  <>
    <strong>{legalEntity}</strong>, a company registered in England and Wales, trading as {businessName} ("we", "us",
    "our")
  </>
);

export function TermsPage() {
  return (
    <LegalLayout title="Terms of Service" intro="The agreement between you and Sir Levantine Academy for the website, the mobile app and every service we provide.">
      <h2>1. Who we are</h2>
      <p>
        These terms are an agreement between you and <Company />. You can contact us at <Mail />. By creating an account,
        purchasing a membership or package, or using the website or the mobile app, you accept these terms.
      </p>

      <h2>2. Our services</h2>
      <ul>
        <li><strong>Academy membership</strong>: a recurring subscription giving access to the video library and members' content.</li>
        <li><strong>AI Deal Analyzer</strong>: answers to property questions, paid for with analyzer credits.</li>
        <li><strong>Mentorship packages</strong>: one-off purchases of mentorship credits (one credit = one hour), booked as sessions with a mentor.</li>
        <li><strong>Deal sourcing</strong>: a separate service under its own written agreement and the published fee scale.</li>
        <li><strong>The Blueprint</strong>: educational documents available to signed-in members.</li>
      </ul>

      <h2>3. Education, not financial advice</h2>
      <p>
        All content, analyses, AI answers and mentorship sessions are for education and information only. We are not
        authorised by the Financial Conduct Authority and do not give regulated financial, investment, tax, mortgage or
        legal advice. Figures are estimates. Property investment carries risk; your capital is at risk and past
        performance does not guarantee future results. Always take independent professional advice before investing.
      </p>

      <h2>4. Your account</h2>
      <ul>
        <li>You must be at least 18 and give accurate details. One account per person; it may not be shared.</li>
        <li>Keep your password secret. You are responsible for activity on your account.</li>
        <li>We may suspend or close accounts that breach these terms, misuse the services or fail to pay.</li>
      </ul>

      <h2>5. Payments</h2>
      <p>
        Prices are shown in pounds sterling and include UK VAT where applicable. Card payments are processed by Stripe;
        we never see or store your full card number. Memberships renew automatically as described in the{" "}
        <Link to="/legal/subscription" className="underline underline-offset-4">Subscription Policy</Link>. Refunds are
        covered by the <Link to="/legal/refunds" className="underline underline-offset-4">Refund Policy</Link>.
      </p>
      <p>
        Purchases cannot be made inside the mobile app. Memberships and packages bought on our website are available
        in the app when you sign in with the same account.
      </p>

      <h2>6. Credits and sessions</h2>
      <ul>
        <li>Credits are personal, have no cash value and cannot be transferred. They remain valid while your account is open.</li>
        <li>A mentorship session uses its credits when it is booked. Cancellations requested at least 24 hours in advance and accepted by us return the credits.</li>
        <li>One analyzer question uses one credit; if a question cannot be answered, the credit is returned automatically.</li>
      </ul>

      <h2>7. Acceptable use and intellectual property</h2>
      <p>
        All videos, documents, templates and software are owned by us or our licensors. You receive a personal,
        non-transferable licence to use them for your own investing. You must not copy, resell, publish, record or
        share content, scrape the services, or attempt to bypass security or usage limits.
      </p>

      <h2>8. Availability and changes</h2>
      <p>
        We aim to keep the services available but do not guarantee uninterrupted access. We may update content and
        features. We will give at least 30 days' notice of price changes to an active membership, and you may cancel
        before the change takes effect.
      </p>

      <h2>9. Liability</h2>
      <p>
        Nothing in these terms limits liability for death or personal injury caused by negligence, fraud, or anything
        that cannot be limited by law, including your statutory rights as a consumer. Otherwise we are not liable for
        investment decisions you make, loss of profit or opportunity, or indirect losses, and our total liability is
        limited to the amount you paid us in the 12 months before the claim.
      </p>

      <h2>10. Ending the agreement</h2>
      <p>
        You can cancel your membership at any time and delete your account from your profile (see{" "}
        <Link to="/legal/delete-account" className="underline underline-offset-4">Delete Account</Link>). We may end the
        agreement on reasonable notice, or immediately for a serious breach.
      </p>

      <h2>11. Law and complaints</h2>
      <p>
        These terms are governed by the laws of England and Wales. If you are a consumer living elsewhere in the UK you
        may also bring proceedings in your local courts. Please contact <Mail /> first so we can try to resolve any
        complaint.
      </p>
    </LegalLayout>
  );
}

export function PrivacyPage() {
  return (
    <LegalLayout title="Privacy Policy" intro="How Sir Levantine Academy collects, uses and protects your personal data under the UK GDPR and the Data Protection Act 2018.">
      <h2>1. Controller</h2>
      <p>
        The data controller is <Company />. Contact us about privacy at <Mail /> (subject "Data request").
      </p>

      <h2>2. Data we collect</h2>
      <ul>
        <li><strong>Account data</strong>: name, email address, phone number, password (stored only as a secure hash).</li>
        <li><strong>Membership and payment data</strong>: plans, packages, credit balances, payment amounts and dates. Card details are handled by Stripe.</li>
        <li><strong>Service data</strong>: mentorship bookings and notes, Deal Analyzer questions and answers, contact form messages.</li>
        <li><strong>Technical data</strong>: IP address, device and browser information, security logs, and the push notification token of the mobile app.</li>
      </ul>

      <h2>3. Why we use it and our lawful bases</h2>
      <ul>
        <li>To provide your account, membership, credits and sessions — <strong>performance of a contract</strong>.</li>
        <li>To take payments, keep accounting records and meet tax rules — <strong>legal obligation</strong>.</li>
        <li>To secure the service, prevent fraud and abuse (including Cloudflare Turnstile bot checks) and improve it — <strong>legitimate interests</strong>.</li>
        <li>To send marketing emails, only where you opted in — <strong>consent</strong>, which you can withdraw at any time.</li>
      </ul>

      <h2>4. Who we share it with</h2>
      <p>We use carefully selected processors bound by data processing terms:</p>
      <ul>
        <li>Stripe (payments), Cloudflare (hosting, storage, video streaming, bot protection),</li>
        <li>our email delivery provider (Brevo or Amazon SES), Cal.com (session scheduling),</li>
        <li>PropertyData (property data used by the Deal Analyzer — questions are sent without your contact details),</li>
        <li>Google Firebase (mobile push notifications), and our IT hosting and logging providers.</li>
      </ul>
      <p>We never sell your personal data.</p>

      <h2>5. International transfers</h2>
      <p>
        Some providers process data outside the UK. Where they do, we rely on UK adequacy regulations or the
        International Data Transfer Addendum / Agreement approved by the ICO.
      </p>

      <h2>6. How long we keep it</h2>
      <ul>
        <li>Account and service data: while your account is open, then deleted or anonymised.</li>
        <li>Payment and invoice records: 6 years after the end of the financial year (HMRC requirements).</li>
        <li>Security logs: up to 90 days. Contact form messages: up to 2 years.</li>
      </ul>

      <h2>7. Your rights</h2>
      <p>
        You have the right to access, correct, delete, restrict or object to the processing of your data, to data
        portability, and to withdraw consent. You can update your details and delete your account yourself in the
        portal. For anything else, email <Mail />; we reply within one month. You may also complain to the Information
        Commissioner's Office (ico.org.uk, 0303 123 1113).
      </p>

      <h2>8. Cookies and storage</h2>
      <p>
        We use only strictly necessary storage: your sign-in session is kept in your browser's local storage, and
        Cloudflare may set security cookies. We do not use advertising cookies.
      </p>

      <h2>9. Children</h2>
      <p>Our services are for adults (18+). We do not knowingly collect data from children.</p>
    </LegalLayout>
  );
}

export function SubscriptionPolicyPage() {
  return (
    <LegalLayout title="Subscription Policy" intro="How the Academy membership is billed, renewed and cancelled.">
      <h2>1. The membership</h2>
      <p>
        The Academy membership costs <strong>£10 per month</strong>, including UK VAT. It gives access to the members'
        video library and members' content for as long as it is active. Mentorship packages and analyzer credits are
        separate one-off purchases.
      </p>

      <h2>2. Automatic renewal</h2>
      <p>
        Your membership starts when your first payment succeeds and <strong>renews automatically every month</strong>{" "}
        on the same date, charging the card you used through Stripe, until you cancel. You will receive a receipt for
        every payment. If a payment fails, Stripe retries it; access ends if the payment cannot be collected.
      </p>

      <h2>3. Cancel anytime</h2>
      <p>
        You can cancel at any time on the website under <strong>Billing → Cancel membership</strong>. Cancelling stops
        future renewals; you <strong>keep access until the end of the period you have already paid for</strong>, after
        which the membership ends. There is no cancellation fee. You can subscribe again at any time.
      </p>

      <h2>4. Price changes</h2>
      <p>
        We will email you at least 30 days before any price change takes effect for an existing membership. If you do
        not agree, you can cancel before the next renewal.
      </p>

      <h2>5. Mobile app</h2>
      <p>
        The mobile app does not sell or manage subscriptions. Manage your membership on our website; the app reflects
        your membership automatically when you sign in. Memberships are not billed through the Apple App Store or Google
        Play, so they cannot be managed or cancelled from your store account.
      </p>

      <h2>6. Questions</h2>
      <p>Contact <Mail /> for help with billing.</p>
    </LegalLayout>
  );
}

export function RefundPage() {
  return (
    <LegalLayout title="Refund Policy" intro="When you can get your money back for memberships, packages and credits.">
      <h2>1. Your 14-day cancellation right</h2>
      <p>
        Under the Consumer Contracts Regulations 2013 you normally have 14 days to cancel a digital service bought
        online. Because our content and credits are available immediately, when you purchase you ask us to start the
        service straight away and acknowledge that you lose the right to cancel once the service has been fully
        provided or the digital content has been accessed. We still apply the fair rules below.
      </p>

      <h2>2. Membership</h2>
      <ul>
        <li>First payment: if you have not watched any Academy video, ask within 14 days for a full refund.</li>
        <li>Renewals: cancel before the renewal date to avoid the next charge. Renewal payments are not refunded for partially used months, but you keep access until the period ends.</li>
      </ul>

      <h2>3. Mentorship packages</h2>
      <ul>
        <li>Unused packages: full refund within 14 days of purchase if no session has been booked.</li>
        <li>Partly used packages: unused credits can be refunded pro rata at our discretion within 90 days of purchase.</li>
        <li>Sessions cancelled by us, or by you at least 24 hours in advance, return the credits to your balance.</li>
      </ul>

      <h2>4. Analyzer credits</h2>
      <p>
        Unused analyzer credit packages can be refunded within 14 days of purchase if no credits from the package have
        been used. Questions that fail are credited back automatically.
      </p>

      <h2>5. Faulty services</h2>
      <p>
        If something we provide is faulty or not as described, you are entitled to a repair, replacement or refund under
        the Consumer Rights Act 2015. These rights are not affected by this policy.
      </p>

      <h2>6. How to ask for a refund</h2>
      <p>
        Email <Mail /> with your account email and the payment date. Approved refunds are made to the original payment
        card through Stripe within 14 days; your bank may take a few more days to show them.
      </p>
    </LegalLayout>
  );
}

export function DataProcessingPage() {
  return (
    <LegalLayout
      title="Data Processing Agreement"
      intro="Terms that apply when Sir Levantine Academy processes personal data on your behalf, and how we manage our own sub-processors."
    >
      <h2>1. Scope and roles</h2>
      <p>
        For your own account we act as <strong>controller</strong> (see the{" "}
        <Link to="/legal/privacy" className="underline underline-offset-4">Privacy Policy</Link>). Where you share
        personal data of third parties with us to receive a service — for example tenant, vendor or joint-venture partner
        details in a deal sourcing brief, mentorship notes or Deal Analyzer questions — you are the controller of that
        data and <Company /> acts as your <strong>processor</strong> under Article 28 UK GDPR. This agreement forms part
        of our Terms of Service.
      </p>

      <h2>2. Our commitments as processor</h2>
      <ul>
        <li>Process the data only on your documented instructions, i.e. to provide the service you requested.</li>
        <li>Ensure everyone with access is bound by confidentiality.</li>
        <li>Apply appropriate technical and organisational measures: encryption in transit (TLS), hashed passwords, access control by role, logging and regular backups.</li>
        <li>Engage sub-processors only under written terms giving equivalent protection, and remain responsible for them.</li>
        <li>Help you respond to data subject requests and with security, breach notification and impact assessments.</li>
        <li>Notify you without undue delay, and in any case within 72 hours, after becoming aware of a personal data breach.</li>
        <li>Delete or return the data at the end of the service, unless the law requires us to keep it.</li>
        <li>Make available the information needed to demonstrate compliance and allow reasonable audits on 30 days' notice.</li>
      </ul>

      <h2>3. Your commitments</h2>
      <p>
        You confirm that you have a lawful basis to share the data with us, that you have informed the people concerned,
        and that you share only what is needed for the service.
      </p>

      <h2>4. Sub-processors</h2>
      <ul>
        <li>Stripe Payments Europe / Stripe, Inc. — payment processing</li>
        <li>Cloudflare, Inc. — hosting, file storage (R2), video streaming, bot protection</li>
        <li>Brevo (Sendinblue SAS) or Amazon Web Services — transactional email</li>
        <li>Cal.com, Inc. — scheduling of mentorship sessions</li>
        <li>PropertyData Ltd — UK property data for the Deal Analyzer</li>
        <li>Google Ireland Ltd (Firebase) — mobile push notifications</li>
      </ul>
      <p>We will update this list before adding a new sub-processor; you may object by contacting us.</p>

      <h2>5. International transfers</h2>
      <p>
        Transfers outside the UK are made only under UK adequacy regulations or the ICO's International Data Transfer
        Agreement / Addendum, with supplementary measures where needed.
      </p>

      <h2>6. Contact</h2>
      <p>
        Data protection queries: <Mail /> (subject "Data request").
      </p>
    </LegalLayout>
  );
}

export function DeleteAccountInfoPage() {
  const { isAuthenticated } = useAuth();
  return (
    <LegalLayout
      title="Delete your account"
      intro="How to delete your Sir Levantine Academy account and the data linked to it — on the website or in the mobile app."
    >
      <h2>Delete it yourself</h2>
      <ul>
        <li><strong>Website</strong>: sign in, open <strong>Profile → Delete account</strong>, confirm with your password.</li>
        <li><strong>Mobile app</strong> (iOS and Android): sign in, open <strong>Profile → Delete account</strong>, confirm with your password.</li>
      </ul>
      <div className="mt-6">
        {isAuthenticated ? (
          <Button asChild variant="destructive">
            <Link to="/account/delete">Delete my account now</Link>
          </Button>
        ) : (
          <Button asChild variant="ink">
            <Link to="/login" search={{ redirect: "/account/delete" }}>
              Sign in to delete your account
            </Link>
          </Button>
        )}
      </div>

      <h2>Can't sign in?</h2>
      <p>
        Email <Mail /> from the address registered on your account with the subject "Delete my account". We may ask you
        to confirm your identity and will complete the request within one month.
      </p>

      <h2>What is deleted</h2>
      <ul>
        <li>Your profile (name, email, phone number) and sign-in details are deleted or irreversibly anonymised.</li>
        <li>Your Deal Analyzer history, mentorship notes and registered devices (push notification tokens) are removed.</li>
        <li>An active membership is cancelled immediately; unused mentorship and analyzer credits are forfeited.</li>
      </ul>

      <h2>What we keep</h2>
      <p>
        Payment and invoice records are kept for 6 years to meet UK tax law, without your profile details attached
        beyond what the law requires. Backups containing your data are overwritten within 35 days.
      </p>
    </LegalLayout>
  );
}
